import { useCallback, useEffect, useState } from "react";
import { Gift, ChevronDown, ChevronRight, Loader2, DollarSign, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";
import { toast } from "sonner";

const PayoutBox = ({ referrer, suggested, onDone }) => {
  const [amount, setAmount] = useState(suggested > 0 ? String(suggested) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a payout amount > 0"); return; }
    setBusy(true);
    try {
      await api.post("/admin/referrals/payout",
        { referrer_account_id: referrer, amount: amt, note },
        { headers: authHeader() });
      toast.success(`Recorded $${amt.toFixed(2)} payout to ${referrer}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
        placeholder="$" data-testid={`ref-admin-payout-amount-${referrer}`}
        className="w-24 rounded border border-vts-line bg-black/40 px-2 py-1.5 text-xs text-white font-mono" />
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="note (optional)" data-testid={`ref-admin-payout-note-${referrer}`}
        className="w-36 rounded border border-vts-line bg-black/40 px-2 py-1.5 text-xs text-white" />
      <button onClick={pay} disabled={busy} data-testid={`ref-admin-payout-btn-${referrer}`}
        className="flex items-center gap-1 rounded-lg border border-vts-profit/50 bg-vts-profit/15 px-3 py-1.5 text-xs font-semibold text-vts-profit hover:bg-vts-profit/25 transition disabled:opacity-40">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />} Mark paid
      </button>
    </div>
  );
};

const CarryoverBox = ({ referrer, onDone }) => {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const amt = Number(amount);
    if (Number.isNaN(amt) || amt < 0) { toast.error("Enter an amount ≥ 0"); return; }
    setBusy(true);
    try {
      await api.post("/admin/referrals/carryover",
        { referrer_account_id: referrer, amount: amt },
        { headers: authHeader() });
      toast.success(`Restored $${amt.toFixed(2)} earned base for ${referrer}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] text-vts-silver/50 font-mono">Restore lost earnings:</span>
      <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
        placeholder="$" data-testid={`ref-admin-carryover-amount-${referrer}`}
        className="w-24 rounded border border-vts-line bg-black/40 px-2 py-1.5 text-xs text-white font-mono" />
      <button onClick={save} disabled={busy} data-testid={`ref-admin-carryover-btn-${referrer}`}
        className="rounded-lg border border-vts-blue/50 bg-vts-blue/15 px-3 py-1.5 text-xs font-semibold text-vts-blue hover:bg-vts-blue/25 transition disabled:opacity-40">
        {busy ? "Saving…" : "Set earned base"}
      </button>
    </div>
  );
};

const ReferrerRow = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`ref-admin-row-${r.referrer_account_id}`} className="rounded-lg border border-vts-line bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-vts-silver/50" /> : <ChevronRight className="h-3.5 w-3.5 text-vts-silver/50" />}
        <span className="font-mono text-xs text-white font-bold">{r.referrer_name || r.referrer_account_id}</span>
        {r.referrer_name && <span className="font-mono text-[10px] text-vts-silver/45">{r.referrer_account_id}</span>}
        {r.payout_requested && (
          <span data-testid={`ref-admin-requested-${r.referrer_account_id}`} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/40">
            PAYOUT REQUESTED
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-[11px] font-mono">
          <span className="text-vts-silver/70">{r.referrals} refs · {r.active_referrals} active · {r.share_pct}%</span>
          <span className="text-vts-silver/70">earned ${r.earned.toFixed(2)}</span>
          <span className="text-vts-silver/50">paid ${r.paid.toFixed(2)}</span>
          <span className={r.outstanding > 0 ? "text-amber-400 font-bold" : "text-vts-silver/50"}>
            owed ${r.outstanding.toFixed(2)}
          </span>
        </span>
      </button>
      {open && (
        <div className="border-t border-vts-line px-4 py-3 space-y-3">
          <div className="space-y-1">
            {r.referred.map((x) => (
              <div key={x.account_id} className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-white">{x.account_id}</span>
                {x.is_demo && <span className="text-[8px] px-1 rounded bg-vts-line text-vts-silver/50">DEMO</span>}
                {x.active && <span className="text-[8px] px-1 rounded bg-vts-profit/10 text-vts-profit">ACTIVE</span>}
                <span className="text-vts-silver/45">{(x.joined || "").slice(0, 10)}</span>
                <span className="ml-auto text-vts-silver/60">markup ${Number(x.markup || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <PayoutBox referrer={r.referrer_account_id} suggested={r.outstanding} onDone={onDone} />
          <CarryoverBox referrer={r.referrer_account_id} onDone={onDone} />
        </div>
      )}
    </div>
  );
};

export const ReferralAdminPanel = ({ onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const resetAll = async () => {
    if (!window.confirm("Reset trades & history? Scoreboards and markup history restart from $0. The REFERRAL LEDGER IS PRESERVED — earned, paid and outstanding amounts stay exactly as they are. This CANNOT be undone.")) return;
    if (!window.confirm("Are you absolutely sure? Final confirmation.")) return;
    setResetting(true);
    try {
      const { data: r } = await api.post("/pro-trade/admin-reset", {}, { headers: authHeader() });
      toast.success(`Reset complete — cleared: ${Object.entries(r.cleared).map(([k, v]) => `${k}:${v}`).join(", ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/referrals", { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  return (
    <div data-testid="referral-admin-panel" className="mt-8 rounded-xl border border-vts-line bg-vts-surface/40 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-amber-400" />
        <h2 className="text-base font-bold text-white">Referral Payout Ledger</h2>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" />}
        <button onClick={load} data-testid="ref-admin-refresh" className="ml-auto text-vts-silver/50 hover:text-white">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ["Referrers", t.referrers, "text-white", "ref-admin-total-referrers"],
            ["Referrals", t.referrals, "text-white", "ref-admin-total-referrals"],
            ["Total paid", `$${t.paid.toFixed(2)}`, "text-vts-silver/80", "ref-admin-total-paid"],
            ["Outstanding", `$${t.outstanding.toFixed(2)}`, "text-amber-400", "ref-admin-total-outstanding"],
          ].map(([label, value, cls, tid]) => (
            <div key={label} data-testid={tid} className="rounded-lg border border-vts-line bg-black/30 p-3">
              <p className={`text-base font-bold font-mono ${cls}`}>{value}</p>
              <p className="text-[9px] uppercase tracking-wider text-vts-silver/50">{label}</p>
            </div>
          ))}
        </div>
      )}
      {(data?.referrers || []).length === 0 ? (
        <p data-testid="ref-admin-empty" className="text-xs text-vts-silver/50">
          No referrals recorded yet. Referrer earnings appear here as users share their links.
        </p>
      ) : (
        <div className="space-y-2">
          {data.referrers.map((r) => (
            <ReferrerRow key={r.referrer_account_id} r={r} onDone={load} />
          ))}
        </div>
      )}
      <p className="text-[10px] text-vts-silver/45">
        Earned = tier share × markup generated by each referrer's REAL referrals ({data?.markup_pct ?? 3}% markup basis).
        Real-money trades only — demo accounts and simulated/dry-run bot trades are excluded from all figures.
        "Mark paid" records a ledger entry — pay the referrer externally (e.g. Deriv transfer), then log it here.
      </p>

      {/* Danger zone — full commission/trade data reset */}
      <div className="border-t border-vts-line pt-3 flex items-center gap-3 flex-wrap">
        <button
          data-testid="ref-admin-reset-btn"
          onClick={resetAll}
          disabled={resetting}
          className="rounded-lg border border-vts-loss/50 bg-vts-loss/10 px-3 py-1.5 text-xs font-semibold text-vts-loss hover:bg-vts-loss/20 transition disabled:opacity-40">
          {resetting ? "Resetting…" : "⚠ Reset ALL trade & commission data"}
        </button>
        <p className="text-[10px] text-vts-silver/45">
          Wipes trades, scoreboards, markup estimates & payout requests. The referral network AND each
          referrer&apos;s earned/paid/owed balances are PRESERVED (earned-so-far is carried over before the wipe). Irreversible.
        </p>
      </div>
    </div>
  );
};

export default ReferralAdminPanel;
